import { EffectTypes, ExpressionTypes } from 'models/enums/Automation';

export type AutomationExpressionValueInputType =
  | 'number'
  | 'text'
  | 'boolean'
  | 'dateTime'
  | 'timeSpan'
  | 'property'
  | 'json';

export type AutomationInputType = AutomationExpressionValueInputType | 'select';

export const automationValueExpressionTypes: Model.Enum.Automation.ExpressionTypes[] = [
  // ExpressionTypes.ValueJson,
  ExpressionTypes.ValueNumeric,
  ExpressionTypes.ValueString,
  ExpressionTypes.ValueBoolean,
  ExpressionTypes.ValueDateTime,
  ExpressionTypes.ValueTimeSpan,
  ExpressionTypes.ValueProperty,
];

const literalExpressionTypes = new Set<Model.Enum.Automation.ExpressionTypes>([
  // ExpressionTypes.ValueJson,
  ExpressionTypes.ValueNumeric,
  ExpressionTypes.ValueString,
  ExpressionTypes.ValueBoolean,
  ExpressionTypes.ValueDateTime,
  ExpressionTypes.ValueTimeSpan,
]);

export function isLiteralExpressionType(type?: Model.Enum.Automation.ExpressionTypes) {
  return Boolean(type && literalExpressionTypes.has(type));
}

export function isValuePropertyExpressionType(type?: Model.Enum.Automation.ExpressionTypes) {
  return type === ExpressionTypes.ValueProperty;
}

export function isValueExpressionType(type?: Model.Enum.Automation.ExpressionTypes) {
  return isLiteralExpressionType(type) || isValuePropertyExpressionType(type);
}

export const automationExpressionValueInputTypes: Partial<
  Record<Model.Enum.Automation.ExpressionTypes, AutomationExpressionValueInputType>
> = {
  [ExpressionTypes.ValueNumeric]: 'number',
  [ExpressionTypes.ValueString]: 'text',
  [ExpressionTypes.ValueBoolean]: 'boolean',
  [ExpressionTypes.ValueDateTime]: 'dateTime',
  [ExpressionTypes.ValueTimeSpan]: 'timeSpan',
  [ExpressionTypes.ValueProperty]: 'property',
  // [ExpressionTypes.ValueJson]: 'json',
};

export function getAutomationExpressionValueInputType(type?: Model.Enum.Automation.ExpressionTypes) {
  return type ? automationExpressionValueInputTypes[type] : undefined;
}

export function getAutomationExpressionTypeDefaultValue(
  type: Model.Enum.Automation.ExpressionTypes | undefined
) {
  switch (getAutomationExpressionValueInputType(type)) {
    case 'boolean':
      return 'false';
    case 'property':
      return null;
    case 'number':
    case 'text':
    case 'dateTime':
    case 'timeSpan':
    case 'json':
      return '';
    default:
      return undefined;
  }
}

export type AutomationOption = {
  id: string | number | boolean;
  title: string;
};

export type AutomationPropertyMetadata = {
  propertyPath: string;
  label: string;
  supportedExpressions: Model.Enum.Automation.ExpressionTypes[];
  inputType?: AutomationInputType;
  options?: AutomationOption[];
  containsValuePath?: string;
  defaultValue?: string | number | boolean;
  getDefaultValue?: (expressionType: Model.Enum.Automation.ExpressionTypes) => string | number | boolean;
  formatValue?: (value: any) => string;
  serializeValue?: (value: any) => string;
  parseValue?: (value: string) => any;
  onValueChange?: (value: any) => void;
};

export type AutomationAssignEffectMetadata = {
  type: 'assign';
  propertyPath: string;
  label: string;
  options?: AutomationOption[];
};

export type AutomationPathIdEffectMetadata = {
  type: 'pathIdMultiSelect';
  propertyPathPrefix: string;
  label: string;
  options?: AutomationOption[];
  value?: string;
  buildPropertyPath: (id: string | number) => string;
  parseIdFromPropertyPath: (propertyPath: string) => string | number | null;
};

export type AutomationEffectMetadata = AutomationAssignEffectMetadata | AutomationPathIdEffectMetadata;

export type AutomationRuleSchema = {
  expressionProperties: string[];
  effectProperties: string[];
  properties: Record<string, AutomationPropertyMetadata>;
  effects: AutomationEffectMetadata[];
  allowMultipleRules?: boolean;
  allowMultipleExpressions?: boolean;
  readonly?: boolean;
  isPropertySelectorDisabled?: boolean;
  defaultExpression?: Partial<Omit<Model.Automation.IExpression, 'uid'>>;
  defaultEffects?: Model.Automation.IEffect[];
};

export type AutomationRuleLike = Model.Automation.IRule & {
  id?: number;
  isDeleted?: boolean;
  [key: string]: any;
};

export const defaultAutomationExpression: Omit<Model.Automation.IExpression, 'uid'> = {
  type: undefined,
  propertyPath: '',
  value: '',
};

export const createAssignEffect = (propertyPath: string, value = ''): Model.Automation.IEffect => ({
  type: EffectTypes.Assign,
  propertyPath,
  value,
});
